(() => {
  if (window.NupInfiniteScrollLoaded) return;
  window.NupInfiniteScrollLoaded = true;

  const CONTAINER_ID = 'ProductGridContainer';
  const GRID_ID = 'product-grid';
  const NEXT_SELECTOR = '.pagination__item--prev[href]';

  class NupInfiniteScroll {
    constructor(container) {
      this.container = container;
      this.grid = container.querySelector('#' + GRID_ID);
      this.sentinel = container.querySelector('[data-nup-infinite-sentinel]');
      this.status = container.querySelector('[data-nup-infinite-status]');
      this.continuity = container.querySelector('[data-nup-collection-continuity]');
      this.loading = false;
      this.done = false;
      this.observer = null;

      if (!this.grid || !this.sentinel || !('IntersectionObserver' in window)) return;

      document.documentElement.classList.add('nup-infinite-scroll-ready');
      this.updateState();
      this.observe();
    }

    disconnect() {
      this.observer?.disconnect();
      this.observer = null;
    }

    getNextUrl() {
      const link = this.container.querySelector(NEXT_SELECTOR);
      return link?.getAttribute('href') || '';
    }

    updateState() {
      const nextUrl = this.getNextUrl();
      this.done = !nextUrl;

      if (this.status) {
        this.status.textContent = this.loading ? 'Cargando más productos…' : '';
      }

      this.sentinel.classList.toggle('is-done', this.done);
      this.sentinel.classList.toggle('is-loading', this.loading);

      if (this.continuity) {
        const shouldShow = this.done && !this.loading;
        this.continuity.hidden = !shouldShow;
        this.continuity.classList.toggle('is-visible', shouldShow);
      }

      if (this.done) {
        this.disconnect();
      }
    }

    observe() {
      if (this.done || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.loadNext();
          }
        },
        { rootMargin: '700px 0px 700px 0px', threshold: 0.01 }
      );

      this.observer.observe(this.sentinel);
    }

    async loadNext() {
      if (this.loading || this.done) return;

      const nextPath = this.getNextUrl();
      if (!nextPath) {
        this.done = true;
        this.updateState();
        return;
      }

      this.loading = true;
      this.updateState();

      try {
        const nextUrl = new URL(nextPath, window.location.origin);
        const sectionId = this.sentinel.dataset.sectionId || this.grid.dataset.id;
        if (sectionId) nextUrl.searchParams.set('section_id', sectionId);

        const response = await fetch(nextUrl.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error('Infinite scroll request failed');

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incomingContainer = parsed.getElementById(CONTAINER_ID);
        const incomingGrid = incomingContainer?.querySelector('#' + GRID_ID);

        if (!incomingContainer || !incomingGrid) {
          throw new Error('Infinite scroll response missing product grid');
        }

        const incomingItems = [...incomingGrid.children];
        incomingItems.forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          item.classList.remove('animate--slide-in');
          item.removeAttribute('data-cascade');
          item.style.removeProperty('--animation-order');
          this.grid.appendChild(item);
        });

        const currentPagination = this.container.querySelector('[data-nup-infinite-pagination]');
        const incomingPagination = incomingContainer.querySelector('[data-nup-infinite-pagination]');

        if (incomingPagination) {
          if (currentPagination) {
            currentPagination.replaceWith(incomingPagination);
          } else {
            this.sentinel.before(incomingPagination);
          }
        } else if (currentPagination) {
          currentPagination.remove();
        }

        this.loading = false;
        this.updateState();

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger();
        }
      } catch (error) {
        console.error('[NUP infinite scroll]', error);
        this.loading = false;
        this.sentinel.classList.remove('is-loading');

        if (this.status) {
          this.status.textContent = 'No pudimos cargar más productos. Usa la paginación para continuar.';
        }

        document.documentElement.classList.remove('nup-infinite-scroll-ready');
        this.disconnect();
      }
    }
  }

  let instance = null;
  let observedContainer = null;
  let mutationObserver = null;
  let frame = 0;

  const mount = () => {
    const container = document.getElementById(CONTAINER_ID);

    if (!container) {
      instance?.disconnect();
      instance = null;
      return;
    }

    const sentinel = container.querySelector('[data-nup-infinite-sentinel]');

    if (
      instance &&
      instance.container === container &&
      instance.sentinel === sentinel
    ) {
      return;
    }

    instance?.disconnect();
    instance = new NupInfiniteScroll(container);
  };

  const scheduleMount = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(mount);
  };

  const watchGrid = () => {
    const container = document.getElementById(CONTAINER_ID);
    if (!container || observedContainer === container) return;

    mutationObserver?.disconnect();
    observedContainer = container;

    mutationObserver = new MutationObserver(scheduleMount);
    mutationObserver.observe(container, { childList: true, subtree: true });
  };

  const boot = () => {
    mount();
    watchGrid();

    const documentObserver = new MutationObserver(() => {
      const current = document.getElementById(CONTAINER_ID);
      if (current !== observedContainer) {
        watchGrid();
        scheduleMount();
      }
    });

    documentObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();